// Tour Context for Magnus Resolve
// Manages product tour state and navigation
// ============================================================
/* eslint-disable react-refresh/only-export-components */

import React, { createContext, useContext, useState, useEffect } from 'react';

export interface TourStep {
    target: string; // CSS selector
    title: string;
    content: string;
    placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
    page?: string; // Which page this step is for
}

interface TourContextType {
    isTourActive: boolean;
    currentStep: number;
    startTour: () => void;
    stopTour: () => void;
    nextStep: () => void;
    previousStep: () => void;
    tourSteps: TourStep[];
}

const TourContext = createContext<TourContextType | undefined>(undefined);

export const useTour = () => {
    const context = useContext(TourContext);
    if (!context) {
        throw new Error('useTour must be used within TourProvider');
    }
    return context;
};

// Define tour steps for Resolve application
const TOUR_STEPS: TourStep[] = [
    {
        target: 'body',
        title: 'Welcome to Magnus Resolve 🛡️',
        content: 'Your comprehensive Reliability Engineering platform. Manage FRACAS incidents, 8D problem solving, and advanced analytics in one unified workspace.',
        placement: 'center',
    },
    // ═══════════════════════════════════════════════════════════════
    // AI TOOLS
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[data-tour-id="magnus-assistant"]',
        title: '🤖 Magnus AI Assistant',
        content: 'Your always-available expert. Ask Magnus to analyze failure trends, suggest root causes, or guide you through an 8D report. Toggle between Basic Navigation and Prime Analytics modes.',
        placement: 'left',
    },
    {
        target: '[data-tour-id="ai-assistant-nav"]',
        title: '🧠 Dedicated AI Workspace',
        content: 'Access the full-page AI Assistant here for complex queries, suggested prompts, and deep-dive analysis of your reliability data.',
        placement: 'right',
    },
    // ═══════════════════════════════════════════════════════════════
    // CORE MODULES
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[href="/fracas"]',
        title: '⚠️ FRACAS Incidents',
        content: 'The central hub for all Failure Reporting, Analysis, and Corrective Action System data. Log, track, and prioritize incidents from field or lab.',
        placement: 'right',
    },
    {
        target: '[href="/8d"]',
        title: '📋 8D Problem Solving',
        content: 'Manage disciplined problem solving with the industry-standard 8D methodology. Create teams, define root causes, and track permanent corrective actions.',
        placement: 'right',
    },
    {
        target: '[href="/lab"]',
        title: '🧪 Lab Management',
        content: 'Track physical samples, test requests, and lab results. Link lab data directly to FRACAS incidents for complete traceability.',
        placement: 'right',
    },
    // ═══════════════════════════════════════════════════════════════
    // ANALYTICS & TOOLS
    // ═══════════════════════════════════════════════════════════════
    {
        target: '[href="/analytics"]',
        title: '📊 Advanced Analytics',
        content: 'Visualize reliability metrics like MTBF, warranty costs, and Pareto charts. Use the Weibull tool for life data analysis.',
        placement: 'right',
    },
    {
        target: '[href="/rca"]',
        title: '🔍 Root Cause Tools',
        content: 'Access powerful RCA tools like 5-Why, Ishikawa (Fishbone), and Fault Tree Analysis (FTA) to drill down to the true source of failures.',
        placement: 'right',
    },
    {
        target: '[data-tour-id="user-guide"]',
        title: '📚 User Guide',
        content: 'Need help? Click here for comprehensive "Application Notes" and detailed feature guides to master Magnus Resolve.',
        placement: 'bottom',
    },
    {
        target: 'body',
        title: 'You\'re Ready to Resolve! 🚀',
        content: 'Start tracking incidents, solving problems, and improving reliability. Magnus is here to help you every step of the way.',
        placement: 'center',
    },
];

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [isTourActive, setIsTourActive] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);

    useEffect(() => {
        // Check if user has seen tour before
        const tourSeen = localStorage.getItem('resolve-tour-completed');
        if (!tourSeen) {
            // Could auto-start tour for new users
        }
    }, []);

    const startTour = () => {
        setIsTourActive(true);
        setCurrentStep(0);
    };

    const stopTour = () => {
        setIsTourActive(false);
        setCurrentStep(0);
        localStorage.setItem('resolve-tour-completed', 'true');
    };

    const nextStep = () => {
        if (currentStep < TOUR_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            stopTour();
        }
    };

    const previousStep = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    return (
        <TourContext.Provider
            value={{
                isTourActive,
                currentStep,
                startTour,
                stopTour,
                nextStep,
                previousStep,
                tourSteps: TOUR_STEPS,
            }}
        >
            {children}
        </TourContext.Provider>
    );
};
